import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import './HeroSectionNew.css';

const HeroSectionNew = () => {
  const heroRef = useRef(null);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: null, y: null });
  const animationRef = useRef(null);

  const [activeWord, setActiveWord] = useState(0);
  const [typedCount, setTypedCount] = useState(0);
  const [phase, setPhase] = useState(0);
  // phase 0: typing line 1
  // phase 1: typing line 2
  // phase 2: done typing, show rest

  const LINE1 = 'Where Researchers';
  const words = ['Collaborate', 'Discover', 'Publish', 'Innovate'];
  const LINE2_SUFFIX = ' Together';



  // Full line 2 text for current word
  const getLine2 = () => words[activeWord] + LINE2_SUFFIX;

  /* ═══════════════ Antigravity Particle Animation ═══════════════ */
  useEffect(() => {
    const canvas = canvasRef.current;
    const hero = heroRef.current;
    if (!canvas || !hero) return;

    const ctx = canvas.getContext('2d');
    const colors = ['#4285F4', '#4285F4', '#4285F4', '#34A853', '#EA4335', '#FBBC04'];

    const resizeCanvas = () => {
      const rect = hero.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      initParticles();
    };

    const initParticles = () => {
      const count = Math.floor((canvas.width * canvas.height) / 15000);
      particlesRef.current = [];

      for (let i = 0; i < Math.min(count, 100); i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          originX: 0,
          originY: 0,
          vx: 0,
          vy: 0,
          size: Math.random() * 3 + 1.5,
          color: colors[Math.floor(Math.random() * colors.length)],
          alpha: Math.random() * 0.5 + 0.2,
        });
        particlesRef.current[i].originX = particlesRef.current[i].x;
        particlesRef.current[i].originY = particlesRef.current[i].y;
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const mouse = mouseRef.current;
      const particles = particlesRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        if (mouse.x !== null && mouse.y !== null) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = 150;

          if (dist < maxDist) {
            const force = (maxDist - dist) / maxDist;
            const angle = Math.atan2(dy, dx);
            p.vx += Math.cos(angle) * force * 2;
            p.vy += Math.sin(angle) * force * 2;
          }
        }

        p.x += p.vx;
        p.y += p.vy;
        p.x += (p.originX - p.x) * 0.015;
        p.y += (p.originY - p.y) * 0.015;
        p.vx *= 0.96;
        p.vy *= 0.96;
        p.vx += (Math.random() - 0.5) * 0.03;
        p.vy += (Math.random() - 0.5) * 0.03;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };

    const handleMouseLeave = () => {
      mouseRef.current.x = null;
      mouseRef.current.y = null;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    hero.addEventListener('mousemove', handleMouseMove);
    hero.addEventListener('mouseleave', handleMouseLeave);
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      hero.removeEventListener('mousemove', handleMouseMove);
      hero.removeEventListener('mouseleave', handleMouseLeave);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  /* ── Typewriter effect - Line 1 then Line 2 ── */
  useEffect(() => {
    if (phase !== 0) return;
    let i = 0;
    const totalChars = LINE1.length + getLine2().length;
    
    const id = setInterval(() => {
      i += 1;
      setTypedCount(i);
      
      // When line 1 is done, switch to phase 1 (typing line 2)
      if (i === LINE1.length) {
        setPhase(1);
      }
      
      // When all typing is done
      if (i >= totalChars) {
        clearInterval(id);
        setTimeout(() => setPhase(2), 400);
      }
    }, 50);
    
    return () => clearInterval(id);
  }, []);

  /* ── Rotating word cycle (only after phase 2) ── */
  useEffect(() => {
    if (phase < 2) return;
    const id = setInterval(() => setActiveWord((p) => (p + 1) % words.length), 2800);
    return () => clearInterval(id);
  }, [phase]);

  /* ── GSAP entrance ── */
  useEffect(() => {
    if (phase < 2) return;
    const ctx = gsap.context(() => {
      gsap.fromTo('.hero-subtitle', { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' });
      gsap.fromTo('.hero-cta-group', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out', delay: 0.1 });
      gsap.fromTo('.hero-trust', { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', delay: 0.2 });
    }, heroRef);
    return () => ctx.revert();
  }, [phase]);

  // Calculate what to show for line 2
  const line2Full = getLine2();
  const line2Typed = phase >= 1 ? typedCount - LINE1.length : 0;

  return (
    <section className="hero-section-new" ref={heroRef}>
      {/* Antigravity-style particle canvas */}
      <canvas ref={canvasRef} className="hero-particles-canvas" />

      <div className="hero-inner">
        <h1 className="hero-title">
          {/* Line 1: Where Researchers - always visible after typed */}
          <span className="hero-title-line hero-title-typed">
            {LINE1.split('').map((ch, i) => (
              <span key={i} className={`hero-char ${i < Math.min(typedCount, LINE1.length) ? 'hero-char--visible' : ''}`}>
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
            {/* Cursor shows on line 1 only during phase 0 */}
            {phase === 0 && <span className="hero-cursor hero-cursor--rainbow" />}
          </span>

          {/* Line 2: [Word] Together */}
          <span className={`hero-title-line hero-title-line--accent ${phase >= 1 ? 'hero-accent--visible' : ''}`}>
            {phase < 2 ? (
              // During typing phase
              <>
                {line2Full.split('').map((ch, i) => (
                  <span key={i} className={`hero-char ${i < line2Typed ? 'hero-char--visible' : ''}`}>
                    {ch === ' ' ? '\u00A0' : ch}
                  </span>
                ))}
                {phase === 1 && <span className="hero-cursor hero-cursor--rainbow" />}
              </>
            ) : (
              // After typing - show rotating word with cursor at end
              <>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={activeWord}
                    className="hero-rotating-word"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    {words[activeWord]}
                  </motion.span>
                </AnimatePresence>
                <span className="hero-title-static">&nbsp;Together</span>
                <span className="hero-cursor hero-cursor--rainbow" />
              </>
            )}
          </span>
        </h1>

        <p className="hero-subtitle">
          The AI-powered research platform. Search 200M+ papers, build private
          labs, join communities, and accelerate discoveries with your team.
        </p>

        <div className="hero-cta-group">
          <Link to="/explore" className="hero-btn hero-btn--primary">
            <span>Explore Papers</span>
            <ArrowRight size={18} strokeWidth={2} />
          </Link>
          <Link to="/signin" className="hero-btn hero-btn--secondary">
            Sign In
          </Link>
        </div>


      </div>
    </section>
  );
};

export default HeroSectionNew;
